import { Box, Checkbox, RadioGroup, SimpleGrid, Stack } from "@chakra-ui/react";
import { observer } from "mobx-react";
import { useStores } from "../../../../shared/stores/RootStore";
import {
  FacilityType,
  FacilityWidth,
  FlowDirectionType,
} from "@keita/lanepatrol-sdk";
import { AdjacentFeaturesForm } from "./AdjacentFeaturesForm";
import { FieldWrapper } from "./FieldWrapper";
import { RadioOptions } from "./RadioOptions";
import { CreateOrUpdateHlaLane } from "../../../models/HlaLane";
import { HlaScoreView } from "../../HlaScoreView";
import { t } from "i18next";
import { hlaAttributeOptions } from "../../../../map/models/HlaAttribute";

const HlaLaneFormBase = ({ lane }: { lane: CreateOrUpdateHlaLane }) => {
  const { hlaStore, projectStore } = useStores();
  const hlaWay = hlaStore.getSelected()!;
  const project = projectStore.getSelected();
  const canEdit = !!project?.permissions?.canCodeHla;
  const directionkey = lane.frontFacing ? "frontFacingLane" : "backFacingLane";

  return (
    <Stack py={2} px={6} gap={6} overflow={"auto"} w={"full"}>
      {/* HlaWay options */}
      <SimpleGrid columns={{ xl: 1, "2xl": 2 }} gap={6}>
        <FieldWrapper label={t(`shared:hlaAttribute.facilityType`)}>
          <RadioGroup.Root
            colorPalette={"primary"}
            disabled={!canEdit}
            invalid={lane.facilityType == null}
            value={lane.facilityType}
            onValueChange={({ value }) => {
              hlaStore.code((prev) => {
                const prevLane = prev[directionkey]!;
                prevLane.facilityType = value as FacilityType;
                if (value === FacilityType.Sidewalk) {
                  prevLane.flowDirection = FlowDirectionType.TwoWay;
                  prevLane.adjacentFeaturesLeft.sidewalk = null;
                  prevLane.adjacentFeaturesRight.sidewalk = null;
                }
                return { ...prev };
              });
            }}
          >
            <RadioOptions options={hlaAttributeOptions.facilityType!} />
          </RadioGroup.Root>
        </FieldWrapper>

        <Stack gap={4}>
          {lane.facilityType !== FacilityType.Sidewalk && (
            <FieldWrapper label={t(`shared:hlaAttribute.flowDirection`)}>
              <RadioGroup.Root
                colorPalette={"primary"}
                disabled={!canEdit}
                value={lane.flowDirection}
                onValueChange={({ value }) => {
                  hlaStore.code((prev) => {
                    const prevLane = prev[directionkey]!;
                    prevLane.flowDirection = value as FlowDirectionType;
                    return { ...prev };
                  });
                }}
              >
                <RadioOptions options={hlaAttributeOptions.flowDirection!} />
              </RadioGroup.Root>
            </FieldWrapper>
          )}

          <FieldWrapper label="Facility width per direction">
            <RadioGroup.Root
              colorPalette={"primary"}
              disabled={!canEdit}
              value={lane.facilityWidthPerDirection}
              onValueChange={({ value }) => {
                hlaStore.code((prev) => {
                  const prevLane = prev[directionkey]!;
                  prevLane.facilityWidthPerDirection = value as FacilityWidth;
                  return { ...prev };
                });
              }}
            >
              <RadioOptions
                options={hlaAttributeOptions.facilityWidthPerDirection!}
              />
            </RadioGroup.Root>
          </FieldWrapper>
        </Stack>

        <FieldWrapper label="Others">
          <Checkbox.Root
            disabled={!canEdit}
            colorPalette="primary"
            checked={lane.lightSegregation}
            onCheckedChange={({ checked }) => {
              hlaStore.code((prev) => {
                const prevLane = prev[directionkey]!;
                prevLane.lightSegregation = checked as boolean;
                return { ...prev };
              });
            }}
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control />
            <Checkbox.Label fontSize={"xs"} fontWeight={"normal"}>
              {t(`shared:hlaAttribute.lightSegregation`)}
            </Checkbox.Label>
          </Checkbox.Root>

          <Checkbox.Root
            disabled={!canEdit}
            colorPalette="primary"
            checked={lane.tramOrTrainRails}
            onCheckedChange={({ checked }) => {
              hlaStore.code((prev) => {
                const prevLane = prev[directionkey]!;
                prevLane.tramOrTrainRails = checked as boolean;
                return { ...prev };
              });
            }}
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control />
            <Checkbox.Label fontSize={"xs"} fontWeight={"normal"}>
              {t(`shared:hlaAttribute.tramOrTrainRails`)}
            </Checkbox.Label>
          </Checkbox.Root>
        </FieldWrapper>
      </SimpleGrid>

      <AdjacentFeaturesForm canEdit={canEdit} />

      <Box py={6}>
        <HlaScoreView score={hlaWay[directionkey]?.score ?? undefined} />
      </Box>
    </Stack>
  );
};

export const HlaLaneForm = observer(HlaLaneFormBase);
